import {
  type ApplicationContent,
  type ApplicationPreview,
  type CreatedEntry,
  downloadFile,
  type LibraryApplication,
  type LibraryImportResult,
  type PmsLibrary,
  type PmsLibrarySource,
  type SubmitOutcome,
} from '@felix/ui';
import { ApiError, api, officeApi } from './api.js';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Ham `.xlsx` gövdesiyle içe aktarma (ADR 0063 §2): en çok 10 MB, kalıcı depoya yazılmaz. */
async function importFile(file: Blob, preview: boolean): Promise<LibraryImportResult> {
  let response: Response;
  try {
    response = await fetch(`/api/office/pms/library/import?preview=${preview}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': XLSX },
      body: file,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  if (!response.ok) {
    throw new ApiError(
      data.error ?? 'INTERNAL',
      response.status,
      typeof data.detail === 'string' ? data.detail : undefined,
      data,
    );
  }
  return data as LibraryImportResult;
}

async function exportFile(): Promise<void> {
  let response: Response;
  try {
    response = await fetch('/api/office/pms/library/export', { credentials: 'same-origin' });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(data.error ?? 'INTERNAL', response.status);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'PMS_Library.xlsx';
  downloadFile(fileName, new Uint8Array(await response.arrayBuffer()), XLSX);
}

/**
 * Şirket PMS kütüphanesi (M15f; ADR 0063): ofis verisi. Gemiye uygulama kurulum devrinde ofisten
 * yazılır; etkin gemide talep olarak gemiye iner (mimari belge §8).
 */
export const officePmsLibrarySource: PmsLibrarySource = {
  load: () => api<PmsLibrary>('/office/pms/library'),
  vessels: async () => (await officeApi.vessels()).map((v) => ({ id: v.id, name: v.name })),
  importFile,
  importTemplate: (preview) =>
    api<LibraryImportResult>('/office/pms/library/template', {
      method: 'POST',
      body: { preview },
    }),
  exportFile,
  updateItem: async (id, body) => {
    await api(`/office/pms/library/items/${encodeURIComponent(id)}`, { method: 'PATCH', body });
  },
  updateJob: async (id, body) => {
    await api(`/office/pms/library/jobs/${encodeURIComponent(id)}`, { method: 'PATCH', body });
  },
  // Elle ekleme (ADR 0102): yetki ve doğrulama sunucuda
  createSystem: (body) =>
    api<CreatedEntry>('/office/pms/library/systems', { method: 'POST', body }),
  createItem: (body) => api<CreatedEntry>('/office/pms/library/items', { method: 'POST', body }),
  createJob: (body) => api<CreatedEntry>('/office/pms/library/jobs', { method: 'POST', body }),
  removeItem: async (id, rowVersion) => {
    await api(`/office/pms/library/items/${encodeURIComponent(id)}?rowVersion=${rowVersion}`, {
      method: 'DELETE',
    });
  },
  removeJob: async (id, rowVersion) => {
    await api(`/office/pms/library/jobs/${encodeURIComponent(id)}?rowVersion=${rowVersion}`, {
      method: 'DELETE',
    });
  },
  previewApplication: (vesselId: string, content: ApplicationContent) =>
    api<ApplicationPreview>('/office/pms/library/applications/preview', {
      method: 'POST',
      body: { vesselId, content },
    }),
  submitApplication: (vesselId, content, note) =>
    api<SubmitOutcome>('/office/pms/library/applications', {
      method: 'POST',
      body: { vesselId, content, ...(note ? { note } : {}) },
    }),
  applications: (vesselId) =>
    api<LibraryApplication[]>(
      `/office/pms/library/applications${vesselId ? `?vesselId=${encodeURIComponent(vesselId)}` : ''}`,
    ),
};
